import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, supabase, checkResourceLimits } from '../../context/AuthContext';
import './Dashboard.css';
import ConversationsService from '../../services/ConversationsService';
import logger from '../../utils/logger';
import LimitWarning from '../../components/common/LimitWarning';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Helper for decoding Unicode
const decodeUnicode = (str) =>
  (typeof str === 'string')
    ? str.replace(/\\u[\dA-F]{4}/gi, match =>
        String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
      )
    : str;

// Helper to process messages from DB
const processMessages = (data = []) => {
  const processed = [];
  const seen = new Set();
  const sorted = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  sorted.forEach(message => {
    if (message.user_query && !seen.has(message.user_query)) {
      seen.add(message.user_query);
      processed.push({
        id: `user-${message.id}`,
        user_query: message.user_query,
        is_user: true,
        created_at: message.created_at,
      });
    }
    if (message.openai_response) {
      processed.push({
        id: message.id,
        openai_response: message.openai_response,
        citations: message.citations,
        is_user: false,
        created_at: message.created_at,
      });
    }
  });
  return processed;
};

// Helper to normalize citations
const normalizeCitations = (rawCitations) => {
  if (!rawCitations) return [];
  if (Array.isArray(rawCitations)) {
    return rawCitations
      .map(cit => {
        if (typeof cit === 'number' || typeof cit === 'string') return { id: cit };
        if (typeof cit === 'object' && cit !== null) return cit;
        return null;
      })
      .filter(Boolean);
  }
  if (typeof rawCitations === 'object') {
    return Object.keys(rawCitations).map(key => ({ id: key, ...rawCitations[key] }));
  }
  return [];
};

const Dashboard = () => {
  const { session } = useContext(AuthContext);
  const { currentAgent } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams();
  const messagesEndRef = useRef(null);
  const token = session?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || '';
  const userId = session?.user?.id;

  // State variables
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCitations, setIsLoadingCitations] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [currentConversation, setCurrentConversation] = useState(null);
  const [limitWarning, setLimitWarning] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [completeResponse, setCompleteResponse] = useState('');
  const completeResponseRef = useRef('');
  const conversationRef = useRef(null);
  const pendingPromptRef = useRef('');
  const [messageId, setMessageId] = useState(null);
  const [citations, setCitations] = useState(null);
  const citationsRef = useRef(null);
  const isSendingRef = useRef(false);
  const tempAiMessageId = useRef(null);

  // Helper to clear state after a message is saved
  const resetMessageState = () => {
    setIsStreaming(false);
    isSendingRef.current = false;
    setTimeout(() => {
      setCompleteResponse('');
      completeResponseRef.current = '';
      setMessageId(null);
      setCitations(null);
      citationsRef.current = null;
      tempAiMessageId.current = null;
    }, 1000);
  };

  useEffect(() => {
  conversationRef.current = currentConversation;
}, [currentConversation]);

  // Fetch citation details (unchanged)
  const fetchCitationDetails = async (citationId) => {
    try {
      const projectId = currentAgent?.project_id || localStorage.getItem('projectId') || 'default';
      const citationUrl = `${API_URL}/api/agent/conversations/citations/${projectId}/${citationId}`;
      const response = await fetch(citationUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to fetch citation: ${response.statusText}`);
      const data = await response.json();
      return { id: citationId, ...data.data };
    } catch (error) {
      logger.error('Error fetching citation details:', error);
      toast.error('Failed to load citation details');
      return null;
    }
  };

  // Helper to update the AI message immediately.
  const updateAiMessage = (updates) => {
    setMessages(prev =>
      prev.map(msg => {
        if (msg.id === tempAiMessageId.current) {
          const newMsg = { ...msg };
          for (const key in updates) {
            if (typeof updates[key] === 'function') {
              newMsg[key] = updates[key](newMsg[key]);
            } else {
              newMsg[key] = updates[key];
            }
          }
          return newMsg;
        }
        return msg;
      })
    );
  };

  // Main sendMessage function (same for existing and new conversations)
  const sendMessage = useCallback(async (message, convSessionId, convId) => {

    if (isSendingRef.current || !message || !convSessionId || !convId) {
      return;
    }
    isSendingRef.current = true;
    setIsStreaming(true);
    setCompleteResponse('');
    completeResponseRef.current = '';

    // Add user and placeholder AI messages
    const tempUserMessageId = `temp-user-${Date.now()}`;
    tempAiMessageId.current = `temp-ai-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      { id: tempUserMessageId, user_query: message, is_user: true, created_at: new Date().toISOString() },
      { id: tempAiMessageId.current, openai_response: '', is_user: false, is_placeholder: true, created_at: new Date().toISOString() },
    ]);

    try {
      const baseUrl = API_URL || window.location.origin;
      const response = await fetch(`${baseUrl}/api/agent/conversations/${convSessionId}/messages?stream=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ prompt: message, chatbot_model: currentAgent?.chatbot_model || 'gpt-4-o', userId }),
      });
      if (!response.ok) throw new Error(`Failed to send message: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processEvent = async (eventType, eventData) => {
        if (eventType === 'progress') {
          try {
            let data = JSON.parse(eventData);
            if (data.status === 'progress' && data.message !== undefined) {
              updateAiMessage({ openai_response: prev => (prev || '') + data.message, is_placeholder: false });
              completeResponseRef.current += data.message;
              setCompleteResponse(completeResponseRef.current);
              return;
            }
          } catch {
            updateAiMessage({ openai_response: prev => (prev || '') + eventData, is_placeholder: false });
            completeResponseRef.current += eventData;
            setCompleteResponse(completeResponseRef.current);
          }
        } else if (eventType === 'finish') {
          let data;
          try {
            data = JSON.parse(eventData);
          } catch (err) {
            logger.warn('Invalid JSON in finish event');
            return;
          }
          if (data.status === 'finish') {
            if (data.id) setMessageId(data.id);
            if (data.citations !== undefined) {
              const normalized = normalizeCitations(data.citations);
              const enriched = await Promise.all(
                normalized.map(cit => (!cit.title ? fetchCitationDetails(cit.id) : cit))
              );
              const finalCitations = enriched.filter(Boolean);
              setCitations(finalCitations);
              citationsRef.current = finalCitations;
              updateAiMessage({ citations: finalCitations });
            }
            updateAiMessage({ openai_response: decodeUnicode(completeResponseRef.current), is_placeholder: false });
            setTimeout(() => { saveMessageAfterStream(); }, 100);
          }
        }
      };

      const eventReader = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            setTimeout(() => { saveMessageAfterStream(); setIsStreaming(false); isSendingRef.current = false; }, 200);
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          for (const event of events) {
            if (!event.trim() || event.trim() === 'data: [DONE]') continue;
            const lines = event.split('\n');
            let eventType = null;
            let eventData = null;
            lines.forEach(line => {
              if (line.startsWith('event:')) eventType = line.substring(6).trim();
              if (line.startsWith('data:')) eventData = line.substring(5).trim();
            });
            if (eventType && eventData) {
              await processEvent(eventType, eventData);
            }
          }
        }
      };

      eventReader();
    } catch (err) {
      logger.error('Error in conversation:', err);
      updateAiMessage({ openai_response: `Error: ${err.message}`, is_error: true });
    } finally {
      setIsStreaming(false);
      isSendingRef.current = false;
    }
  }, [API_URL, token, userId, currentAgent, fetchCitationDetails]);

  // Save the final message to DB
  const saveMessageAfterStream = useCallback(async () => {
    let finalResponse = completeResponseRef.current || completeResponse;
    if (!finalResponse.trim()) {
      finalResponse = 'The AI generated an empty response.';
    }
    const aiMessage = messages.find(msg => msg.id === tempAiMessageId.current);
    let promptToSave = currentMessage;
    const userIndex = messages.findIndex(msg => msg.id === tempAiMessageId.current) - 1;
    if (userIndex >= 0 && messages[userIndex]?.is_user) {
      promptToSave = messages[userIndex].user_query;
    }

    if (aiMessage?.saved_to_db) return;
    try {
      const citationsToSave = citationsRef.current ? [...citationsRef.current] : [];
      await ConversationsService.updateMessage({
        sessionId,
        conversationId: conversationRef.current?.id,
        prompt: pendingPromptRef.current,
        response: finalResponse,
        citations: citationsToSave,
        messageId,
        userId,
      });
      setMessages(prev =>
        prev.map(msg => msg.id === tempAiMessageId.current ? { ...msg, saved_to_db: true } : msg)
      );
      logger.info('Message saved successfully after streaming');
    } catch (error) {
      logger.error('Error saving message:', error);
      toast.error('Failed to save message after streaming');
    } finally {
      resetMessageState();
    }
  }, [sessionId, currentConversation, completeResponse, citations, messageId, userId, messages, currentMessage]);

  // Handle input and key events
  const handleInputChange = (e) => setCurrentMessage(e.target.value);
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  // Handle sending message (with resource limit check)
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !currentAgent || isStreaming) return;
    // Capture the current prompt
    pendingPromptRef.current = currentMessage.trim();
    const userMessage = currentMessage.trim();
    setIsLoadingCitations(true);
    try {
      const limitResult = await checkResourceLimits(session, 'query');
      setLimitWarning(limitResult);
      if (!limitResult.allowed) { setIsLoadingCitations(false); return; }
      setLimitWarning(null);
      setCurrentMessage('');
      if (sessionId && currentConversation) {
        // Existing conversation: simply send the message.
        sendMessage(userMessage, sessionId, currentConversation.id);
      } else {
        // New conversation: create it and then navigate with state.
        setIsLoading(true);
        const baseUrl = API_URL || window.location.origin;
        const response = await fetch(`${baseUrl}/api/agent/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            name: userMessage.substring(0, 30) + (userMessage.length > 30 ? '...' : ''),
            projectId: currentAgent.project_id,
          }),
        });
        if (!response.ok) throw new Error(`Failed to create conversation: ${response.statusText}`);
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          const newSessionId = data.data.session_id;
        
          // Fetch the full conversation details from Supabase
          const { data: conversationData, error } = await supabase
            .from('conversations')
            .select('*')
            .eq('session_id', newSessionId)
            .single();
            
          if (error) throw error;
                    
          // Navigate with the full conversation data
          navigate(`/dashboard/conversation/${newSessionId}`, {
            state: { 
              pendingMessage: userMessage,
              newConversation: conversationData
            }
          });
        } else {
          throw new Error(data.message || 'Failed to create conversation');
        }
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      setErrorMessage(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      setIsLoadingCitations(false);
    }
  };

  // In the useEffect for pending messages
  useEffect(() => {
    const handlePendingMessage = async () => {
      if (location.state?.pendingMessage && location.state?.newConversation) {
        const { pendingMessage, newConversation } = location.state;
        
        // Set the current conversation and wait for it to be set
        setCurrentConversation(newConversation);
        
        // Wait a bit longer to ensure everything is ready
        await new Promise(resolve => setTimeout(resolve, 500));

        // Send the message
        await sendMessage(pendingMessage, newConversation.session_id, newConversation.id);

        // Clear the location state to prevent duplicate sends
        navigate(location.pathname, { replace: true });
      }
    };
    handlePendingMessage();
  }, [location.state, navigate, sendMessage, currentAgent]); // Added currentAgent to dependencies


  // Auto-scroll on messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch conversation details when sessionId changes
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setCurrentConversation(null);
      return;
    }
    let isMounted = true;
    const fetchConversation = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('conversations')
          .select('*')
          .eq('session_id', sessionId)
          .single();
        if (error) throw error;
        if (isMounted) {
          setCurrentConversation(data);
        }
      } catch (err) {
        logger.error('Error fetching conversation:', err);
        if (isMounted) setErrorMessage('Failed to load conversation');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchConversation();
    return () => { isMounted = false; };
  }, [sessionId]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (!sessionId || !currentConversation?.id) return;
    let isMounted = true;
    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', currentConversation.id)
          .order('created_at', { ascending: true });
        if (error) throw error;
        if (isMounted) setMessages(processMessages(data));
      } catch (err) {
        logger.error('Error fetching messages:', err);
        if (isMounted) setErrorMessage('Failed to load messages');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchMessages();
    return () => { isMounted = false; };
  }, [sessionId, currentConversation]);

  return (
    <div className="dashboard">
      <div className="chat-container">
        <div className="messages-container">
          {isLoading && <div className="loading-message">Loading messages...</div>}
          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {messages.length === 0 && !isLoading && <div className="empty-state"><p>No messages yet. Start a conversation!</p></div>}
          <div className="messages-list">
            {messages.map(message => (
              <div key={message.id} className={`message ${message.is_user ? 'user-message' : 'ai-message'} ${message.is_error ? 'error-message' : ''} ${message.is_placeholder ? 'streaming' : ''}`}>
                {message.is_user ? (
                  <div className="message-content user">
                    <div className="message-text">{message.user_query}</div>
                    <div className="message-timestamp">{new Date(message.created_at).toLocaleTimeString()}</div>
                  </div>
                ) : (
                  <div className="message-content ai">
                    <div className="message-text">
                      {message.is_placeholder ? (
                        <>Thinking<span className="loading-dots"><span>.</span><span>.</span><span>.</span></span></>
                      ) : (
                        <ReactMarkdown 
                          breaks={true}
                          components={{
                            root: ({node, ...props}) => <div className="markdown-content" {...props} />,
                            code({node, inline, className, children, ...props}) {
                              const match = /language-(\w+)/.exec(className || '');
                              return !inline && match ? (
                                <SyntaxHighlighter
                                  style={atomDark}
                                  language={match[1]}
                                  PreTag="div"
                                  {...props}
                                >
                                  {String(children).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                              ) : (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              );
                            }
                          }}
                        >
                          {decodeUnicode(message.openai_response)}
                        </ReactMarkdown>
                      )}
                    </div>
                    {message.citations && message.citations.length > 0 && (
                      <div className="citations">
                        <div className="citation-heading">Citations:</div>
                        <ul>
                          {message.citations.map((citation, idx) => (
                            <li key={idx}>
                              {citation.url ? (
                                <a href={citation.url} target="_blank" rel="noopener noreferrer" className="citation-link">
                                  {citation.title || citation.url || `Source ${idx + 1}`}
                                </a>
                              ) : (
                                <span className="citation-text">{citation.title || `Source ${idx + 1}`}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="message-timestamp">{new Date(message.created_at).toLocaleTimeString()}</div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
        <div className="input-container">
          {limitWarning && !limitWarning.allowed && <LimitWarning limitCheck={limitWarning} resourceType="query" isError={true} />}
          <textarea value={currentMessage} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="Type your message here..." disabled={isStreaming} />
          <button type="button" className="send-button" onClick={handleSendMessage} disabled={!currentMessage.trim() || isStreaming || isLoadingCitations}>
            {isStreaming ? <div className="loader"></div> : isLoadingCitations ? <div className="checking-loader"></div> : (
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
